import React from 'react';
import Icon from '../../../components/AppIcon';

// ═══════════════════════════════════════════════════════════════
// Curated resource lookup — NEVER use LLM-generated URLs
// ═══════════════════════════════════════════════════════════════
const CURATED_RESOURCES = {
  'dp': { label: 'DP Guide', url: 'https://leetcode.com/tag/dynamic-programming/', icon: 'Code' },
  'dynamic programming': { label: 'DP Guide', url: 'https://leetcode.com/tag/dynamic-programming/', icon: 'Code' },
  'graphs': { label: 'Graphs', url: 'https://leetcode.com/tag/graph/', icon: 'Code' },
  'trees': { label: 'Trees', url: 'https://leetcode.com/tag/tree/', icon: 'Code' },
  'arrays': { label: 'Arrays', url: 'https://leetcode.com/tag/array/', icon: 'Code' },
  'strings': { label: 'Strings', url: 'https://leetcode.com/tag/string/', icon: 'Code' },
  'linked list': { label: 'Lists', url: 'https://leetcode.com/tag/linked-list/', icon: 'Code' },
  'binary search': { label: 'Search', url: 'https://leetcode.com/tag/binary-search/', icon: 'Code' },
  'sorting': { label: 'Sorting', url: 'https://leetcode.com/tag/sorting/', icon: 'Code' },
  'recursion': { label: 'Recursion', url: 'https://leetcode.com/tag/recursion/', icon: 'Code' },
  'hash table': { label: 'Hashing', url: 'https://leetcode.com/tag/hash-table/', icon: 'Code' },
  'system design': { label: 'Design Primer', url: 'https://github.com/donnemartin/system-design-primer', icon: 'Layout' },
  'scalability': { label: 'Design Primer', url: 'https://github.com/donnemartin/system-design-primer', icon: 'Layout' },
  'communication': { label: 'STAR Guide', url: 'https://www.themuse.com/advice/star-interview-method', icon: 'MessageSquare' },
  'star method': { label: 'STAR Guide', url: 'https://www.themuse.com/advice/star-interview-method', icon: 'MessageSquare' },
  'time management': { label: 'Time Tips', url: 'https://www.interviewcake.com/article/python/coding-interview-tips', icon: 'Clock' },
  'confidence': { label: 'Confidence', url: 'https://hbr.org/2022/11/how-to-overcome-interview-anxiety', icon: 'Zap' },
  'code quality': { label: 'Clean Code', url: 'https://github.com/ryanmcdermott/clean-code-javascript', icon: 'CheckSquare' },
  'big-o': { label: 'Big-O Sheet', url: 'https://www.bigocheatsheet.com/', icon: 'BarChart3' },
  'complexity': { label: 'Big-O Sheet', url: 'https://www.bigocheatsheet.com/', icon: 'BarChart3' },
};

const getResourceForTopic = (title, description, topics) => {
  const searchText = `${title} ${description} ${(topics || []).join(' ')}`.toLowerCase();
  const matched = [];

  for (const [keyword, resource] of Object.entries(CURATED_RESOURCES)) {
    if (searchText.includes(keyword) && !matched.find(m => m.url === resource.url)) {
      matched.push(resource);
    }
  }
  return matched.slice(0, 2);
};

const ImprovementRoadmap = ({ recommendations = [] }) => {
  if (!recommendations || recommendations.length === 0) return null;

  const priorityOrder = { high: 0, medium: 1, low: 2 };
  const sortedRecs = [...recommendations].sort(
    (a, b) => (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2)
  );

  const getPriorityStyle = (p) => {
    if (p === 'high') {
      return {
        color: 'text-rose-500 bg-rose-50/50 border-rose-100/50 dark:bg-rose-500/10 dark:border-rose-500/20 dark:text-rose-400',
        label: 'High'
      };
    }
    if (p === 'medium') {
      return {
        color: 'text-amber-500 bg-amber-50/50 border-amber-100/50 dark:bg-amber-500/10 dark:border-amber-500/20 dark:text-amber-400',
        label: 'Medium'
      };
    }
    return {
      color: 'text-blue-500 bg-blue-50/50 border-blue-100/50 dark:bg-blue-500/10 dark:border-blue-500/20 dark:text-blue-400',
      label: 'Nice to Have'
    };
  };

  const buildPracticeLink = (rec) => {
    if (rec.nextInterview && rec.nextInterview.startsWith('/')) {
      return rec.nextInterview;
    }

    const params = new URLSearchParams();
    const text = `${rec.title} ${rec.description} ${(rec.topics || []).join(' ')}`.toLowerCase();
    if (text.includes('system design') || text.includes('scalability')) {
      params.set('type', 'system-design');
    } else if (text.includes('communication') || text.includes('star') || text.includes('behavioral')) {
      params.set('type', 'behavioral');
    } else {
      params.set('type', 'coding');
    }
    return `/interview-setup?${params.toString()}`;
  };

  return (
    <div className="mt-8">
      {/* Section Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-slate-900 text-white dark:bg-white/10 dark:border dark:border-white/10">
          <Icon name="Map" size={16} />
        </div>
        <div className="space-y-0.5 text-left">
          <h3 className="text-xs font-black text-slate-900 dark:text-whitetracking-wider">Improvement Roadmap</h3>
          <p className="text-[9px] text-slate-400 font-bold">{sortedRecs.length} personalized action items</p>
        </div>
      </div>

      {/* Tightly-packed Compact 2-Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sortedRecs.map((rec, idx) => {
          const style = getPriorityStyle(rec.priority);
          const impact = rec.impactEstimate || Math.max(3, 12 - idx * 2);
          const curatedResources = getResourceForTopic(rec.title, rec.description, rec.topics);
          const practiceLink = buildPracticeLink(rec);

          return (
            <div
              key={idx}
              className="bg-white/60 dark:bg-white/[0.015] backdrop-blur-[12px] border border-slate-200/50 dark:border-white/5 rounded-[1.2rem] p-4 hover:border-emerald-500/20 dark:hover:border-emerald-500/20 transition-all duration-300 text-left flex flex-col justify-between"
            >
              <div className="space-y-2">
                {/* Header: Compact Inline Milestone Node & Priority */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center text-[9px] font-black shrink-0">
                      {idx + 1}
                    </span>
                    <h4 className="text-[13px] font-black text-slate-900 dark:text-white truncate">
                      {rec.title}
                    </h4>
                  </div>
                  <span className={`text-[7px] font-black border px-2 py-0.5 roundedtracking-widest shrink-0 ${style.color}`}>
                    {style.label}
                  </span>
                </div>

                {/* Body description */}
                <p className="text-[10px] sm:text-[11px] text-slate-500 dark:text-slate-400 font-semibold leading-relaxed line-clamp-2">
                  {rec.description}
                </p>

                {/* Topics / Skills Tags */}
                {rec.topics && rec.topics.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {rec.topics.map((topic, tIdx) => (
                      <span key={tIdx} className="text-[8px] font-bold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-white/[0.02] border border-slate-100/60 dark:border-white/5 rounded px-1.5 py-0.5">
                        {topic}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Space-optimized Bottom Action Toolbar */}
              <div className="flex items-center justify-between gap-3 pt-3 mt-3 border-t border-slate-100/60 dark:border-white/5">
                {/* Impact Estimate */}
                <div className="flex items-center gap-1 bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100/40 dark:border-emerald-500/20 rounded px-2 py-0.5 shrink-0">
                  <Icon name="TrendingUp" size={10} className="text-emerald-500 dark:text-emerald-400" />
                  <span className="text-[9px] font-black text-emerald-600 dark:text-emerald-400">+{impact}% potential</span>
                </div>

                {/* Resources & CTAs */}
                <div className="flex items-center gap-1.5 overflow-hidden">
                  {curatedResources.map((r, rIdx) => (
                    <a
                      key={rIdx}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-[8px] font-black text-sky-500 hover:text-sky-600 dark:text-sky-400 dark:hover:text-sky-300 transition-colorstracking-widest bg-sky-50/50 dark:bg-sky-500/10 border border-sky-100/50 dark:border-sky-500/20 rounded px-1.5 py-0.5"
                    >
                      <Icon name={r.icon || "ExternalLink"} size={8} />
                      {r.label}
                    </a>
                  ))}

                  <a
                    href={practiceLink}
                    className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-black rounded px-4 py-1.5 transition-all hover:scale-105 active:scale-95 shrink-0"
                  >
                    <Icon name="Play" size={11} className="text-slate-900" />
                    <span className="text-[10px] font-blacktracking-wider text-slate-900">Practice</span>
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ImprovementRoadmap;